import { useState, useCallback } from "react";
import {
  createInstanceOrder,
  verifyPaymentAndCreateInstance,
} from "../apis/payment";

const usePayment = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const processInstancePayment = useCallback(
    async (planType, instanceName, userInfo = {}) => {
      setIsProcessing(true);
      setError(null);

      try {
        // Call the createInstanceOrder API
        const orderResult = await createInstanceOrder(planType, instanceName);

        if (!orderResult.success) {
          throw new Error(orderResult.message || "Failed to create order");
        }

        // Handle free trial plan: no Razorpay, instance created immediately
        if (planType === "free_trial") {
          // The API returns instance data directly in orderResult.data (adjust to your backend response shape)
          setIsProcessing(false);
          return {
            success: true,
            data: {
              instance: orderResult.data.instance_id
                ? orderResult.data
                : orderResult.data, // adjust if your free trial data is nested differently
            },
          };
        }

        // For paid plans - ensure Razorpay SDK is loaded
        if (!window.Razorpay) {
          throw new Error("Razorpay SDK not loaded");
        }

        // Set up Razorpay options for payment
        return new Promise((resolve, reject) => {
          const options = {
            key: orderResult.data.key,
            amount: orderResult.data.amount,
            currency: orderResult.data.currency,
            name: "Ping By BluTec.ai",
            description: orderResult.data.planName,
            order_id: orderResult.data.orderId,
            prefill: {
              name: userInfo.name || "User",
              email: userInfo.email || "user@example.com",
              contact: userInfo.phone || "",
            },
            theme: { color: "#ea580c" },
            handler: async function (response) {
              try {
                // Verify the payment and create instance
                const verifyResult = await verifyPaymentAndCreateInstance({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                });

                if (verifyResult.success) {
                  setIsProcessing(false);
                  resolve({
                    success: true,
                    data: {
                      ...response,
                      instance: verifyResult.data,
                    },
                  });
                } else {
                  throw new Error(
                    verifyResult.message || "Payment verification failed"
                  );
                }
              } catch (error) {
                setIsProcessing(false);
                setError(error.message);
                reject(error);
              }
            },
            modal: {
              ondismiss: function () {
                setIsProcessing(false);
                reject(new Error("Payment cancelled by user"));
              },
            },
          };

          const razorpayInstance = new window.Razorpay(options);

          razorpayInstance.on("payment.failed", function (response) {
            setIsProcessing(false);
            const error = new Error(
              response.error.description || "Payment failed"
            );
            setError(error.message);
            reject(error);
          });

          razorpayInstance.open();
        });
      } catch (error) {
        setIsProcessing(false);
        setError(error.message);
        throw error;
      }
    },
    []
  );

  // Memoize clearError to prevent recreation on every render
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    processInstancePayment,
    isProcessing,
    error,
    clearError,
  };
};

export default usePayment;
